/**
 * Formateadores extraidos del HTML actual.
 * Locale es-CO por default.
 */

export function fmtNum(n: number | null | undefined, decimals = 2): string {
  if (n == null || n === undefined) return "";
  return Number(n).toLocaleString("es-CO", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function fmtMoney(n: number | null | undefined, currency = "USD"): string {
  if (n == null) return "";
  return `${currency} ${Number(n).toLocaleString("es-CO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

/**
 * Formato corto de fecha del PMIS: dd/mm/aa (ej. 07/05/26).
 *
 * Es el ÚNICO formato de fecha del PMIS — toda fecha que se muestre al
 * usuario pasa por aquí. Las cadenas date-only "YYYY-MM-DD" se interpretan
 * en hora LOCAL (no UTC) para evitar el corrimiento de un día en zonas
 * horarias detrás de UTC (p. ej. Colombia, UTC-5).
 */
export function fmtDateShort(d: Date | string | null | undefined): string {
  if (!d) return "—";
  let date: Date;
  if (typeof d === "string") {
    // "YYYY-MM-DD" puro → forzar medianoche local; el resto, parseo normal.
    date = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + "T00:00:00") : new Date(d);
  } else {
    date = d;
  }
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const mon = String(date.getMonth() + 1).padStart(2, "0");
  const yr = String(date.getFullYear()).slice(-2);
  return `${day}/${mon}/${yr}`;
}

/**
 * Fecha + hora del PMIS: dd/mm/aa HH:MM (ej. 16/05/26 14:32).
 * Reutiliza fmtDateShort para la parte de fecha.
 */
export function fmtDateTimeShort(d: Date | string | null | undefined): string {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return "—";
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${fmtDateShort(date)} ${hh}:${mm}`;
}

/** Devuelve todas las fechas YYYY-MM-DD entre inicio y fin (inclusive). */
export function projectDates(inicio: string, fin: string): string[] {
  const out: string[] = [];
  const start = new Date(inicio);
  const end = new Date(fin);
  const cur = new Date(start);
  while (cur <= end) {
    const iso = cur.toISOString().slice(0, 10);
    if (iso !== undefined) out.push(iso);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** Devuelve todos los meses YYYY-MM entre inicio y fin (inclusive). */
export function projectMonths(inicio: string, fin: string): string[] {
  const out: string[] = [];
  if (!inicio || !fin) return out;
  const startParts = inicio.split("-");
  const endParts = fin.split("-");
  if (startParts.length < 2 || endParts.length < 2) return out;
  let y = Number(startParts[0]);
  let m = Number(startParts[1]);
  const yEnd = Number(endParts[0]);
  const mEnd = Number(endParts[1]);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(yEnd) || Number.isNaN(mEnd)) return out;
  while (y < yEnd || (y === yEnd && m <= mEnd)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return out;
}

/** Etiqueta corta de mes "ene-26" desde "2026-01". */
export function fmtMesShort(yyyymm: string): string {
  const meses = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  const parts = yyyymm.split("-");
  if (parts.length < 2) return yyyymm;
  const y = parts[0];
  const m = Number(parts[1]);
  if (!y || Number.isNaN(m) || m < 1 || m > 12) return yyyymm;
  return `${meses[m - 1]}-${y.slice(-2)}`;
}

/** Genera un id corto unico (no UUID, util para items locales). */
export function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

/** Escapa HTML para evitar XSS al inyectar texto en innerHTML. */
export function escapeHtml(s: unknown): string {
  if (s == null) return "";
  const str = String(s);
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  return str.replace(/[&<>"']/g, (c) => map[c] ?? c);
}
